import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicSharedWord, vocabularyService } from '../../api/vocabularyService';
import { parseExplanation } from '../../utils/explanationMetadata';
import { renderExample } from '../../utils/exampleHtml';
import { useAuth } from '../../context/AuthContext';
import '../../styles/publicCollection.css';
import logo from '../../assets/logo.png';
import Footer from '../common/footer';
import { PUBLIC_APP_ORIGIN } from '../../config/publicUrls';
import { useShareData } from '../../hooks/useShareData';
import ShareHead, { ShareNotFoundHead } from '../common/ShareHead';
import { joinTranslations, wordPageText } from '../../utils/shareText';

const PublicWordPage: React.FC = () => {
    const { t } = useTranslation('vocabulary');
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const authContext = useAuth();
    const isLoggedIn = !!authContext?.user;
    const { data: item, loading, failed } = useShareData<PublicSharedWord>(`/w/${token}`, () =>
        vocabularyService.getPublicByShareToken(token ?? ''));

    if (loading) {
        return (
            <div className="pc-container">
                <div className="pc-loading">{t('public_word.loading')}</div>
            </div>
        );
    }

    if (failed || !item) {
        return (
            <div className="pc-container">
                <ShareNotFoundHead />
                <div className="pc-error">
                    <h2>{t('public_word.title_unavailable')}</h2>
                    <p>{t('public_word.not_available')}</p>
                    <button className="pc-btn" onClick={() => navigate('/')}>{t('public_word.go_home')}</button>
                </div>
            </div>
        );
    }

    const translation = joinTranslations(item.translation);
    const { title: pageTitle, description: pageDesc } = wordPageText(item.word, translation, item.explanation);
    const explanation = parseExplanation(item.explanation);

    const wordUrl = `${PUBLIC_APP_ORIGIN}/w/${token}`;

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'DefinedTerm',
        name: item.word,
        description: pageDesc,
        url: wordUrl,
        inLanguage: item.language,
        inDefinedTermSet: { '@type': 'DefinedTermSet', name: 'WeaveWords', url: `${PUBLIC_APP_ORIGIN}/` },
    };

    return (
        <>
            <ShareHead title={pageTitle} description={pageDesc} url={wordUrl} noIndex={!translation} jsonLd={jsonLd} />

            <div className="pc-container">
                {!isLoggedIn && (
                    <header className="pc-header">
                        <div className="pc-header-inner">
                            <img src={logo} alt="WeaveWords" className="pc-logo" onClick={() => navigate('/')} />
                            <button className="pc-header-cta" onClick={() => navigate('/register')}>
                                {t('public_word.register_cta')}
                            </button>
                        </div>
                    </header>
                )}

                <main className="pc-main">
                    <div className="pc-hero">
                        <div className="pc-badges">
                            {item.language && <span className="pc-badge">{item.language}</span>}
                        </div>
                        <h1 className="pc-title">{item.word}</h1>
                        {translation && <p className="pc-desc">{translation}</p>}
                        {/* The definition is what the description quotes, so it's shown too. */}
                        {explanation.definition && <p className="pc-desc">{explanation.definition}</p>}
                        {explanation.freeText.trim() && <p className="pc-desc">{explanation.freeText}</p>}
                    </div>

                    {item.examples && item.examples.length > 0 && (
                        <section className="pc-words">
                            <h2 className="pc-words-title">{t('public_word.examples')}</h2>
                            <ol className="pcw-examples-list">
                                {item.examples.map((example, i) => (
                                    <li key={i} className="pcw-example-row">{renderExample(example)}</li>
                                ))}
                            </ol>
                        </section>
                    )}

                    <section className={isLoggedIn ? 'pcw-add-word-promo' : 'pc-promo'}>
                        {isLoggedIn ? (
                            <>
                                <h2>{t('public_word.logged_in_title')}</h2>
                                <button
                                    className="pc-btn pc-btn-primary"
                                    onClick={() => navigate('/addWord', { state: { word: item.word } })}
                                >
                                    {t('public_word.add_word')}
                                </button>
                            </>
                        ) : (
                            <>
                                <h2>{t('public_word.logged_out_title')}</h2>
                                <p>{t('public_word.logged_out_body')}</p>
                                <div className="pc-actions">
                                    <button className="pc-btn pc-btn-primary" onClick={() => navigate('/register')}>
                                        {t('public_word.register_cta')}
                                    </button>
                                    <button className="pc-btn" onClick={() => navigate('/login')}>
                                        {t('public_word.already_have_account')}
                                    </button>
                                </div>
                            </>
                        )}
                    </section>
                </main>

                <Footer />
            </div>
        </>
    );
};


export default PublicWordPage;
